/**
 * Project bundle serialization — pack a single project (or every
 * project) into a self-contained JSON blob and unpack it back.
 *
 * Two bundle kinds:
 *
 *   - **project** — one project + every macro/sequencer it references.
 *     Matrix is intentionally NOT included (matrix is rig-wide config,
 *     it doesn't belong to one project). On import: mapping fields on
 *     each item come through verbatim, but the rig's matrix is whatever
 *     the importer's machine has. We surface this in the import dialog.
 *
 *   - **all** — every project + every library (macros, sequencers,
 *     matrix). For full machine-to-machine moves. Restore wipes the
 *     destination's matrix.
 *
 * Bundles are versioned and forward-compatible: unknown fields are
 * preserved on round-trip, missing optional fields default in.
 */

import { projects as allProjects, restoreProject, insertProject, activeProjectId, switchProject, type Project } from '../layout/projectStore.ts';
import type { MacroLibrary, SequencerLibrary, MatrixLibrary, Timeline, RigStateLibrary } from '@opbuttons/schema';
import { macroLibrary, applySyncedMacros } from '../macros/store.ts';
import { sequencerLibrary, applySyncedSequencers } from '../sequencer/store.ts';
import { matrixLibrary, setMatrixLibrary } from '../matrix/store.ts';
import { timelineLibrary, applySyncedTimelines } from '../timeline/store.ts';
import { timelinesForProject } from '../timeline/librarySync.ts';
import { rigStateLibrary, setRigStateLibrary } from '../rigState/store.ts';
import { bridges, restoreBridges } from '../opbridge/store.ts';
import { setActiveSkin, setBarPosition, setEditTriggersEnabled, SKINS, type SkinName } from '../settings/store.ts';
import { setStageDim } from '../performance/store.ts';
import { setThreshold, type PreflightThresholds } from '../performance/preflightConfig.ts';
import { setHelixSetlistOverride } from '../helix/library.ts';
import {
  BUNDLE_SCHEMA,
  parseBundle,
  type ProjectBundle,
  type AllBundle,
} from './validate.ts';
import { mergeRigStateBanks, planBridgeRestore, RESTORABLE_SETTING_KEYS, settingsToRestore } from './restoreMerge.ts';

// Re-export the pure types + validators so existing callers keep
// importing from this module unchanged (SettingsPanel etc. don't
// need to know the split happened).
export {
  BUNDLE_SCHEMA,
  type Bundle,
  type ProjectBundle,
  type AllBundle,
  type BundleValidation,
  validateBundle,
  parseBundle,
} from './validate.ts';

const ORIGIN = `OPButtons PWA · ${navigator.userAgent.split(' ').slice(-1)[0] ?? 'unknown'}`;

// ─── Build (export) ──────────────────────────────────────────────

/** Pull the macroIds + sequencerIds referenced anywhere in this
 *  project's pages. Returns sets so de-dupe is automatic. */
function collectReferencedIds(project: Project): { macroIds: Set<string>; sequencerIds: Set<string> } {
  const macroIds = new Set<string>();
  const sequencerIds = new Set<string>();
  for (const page of project.pages) {
    for (const item of page.items) {
      if (item.kind === 'macro') macroIds.add(item.macroId);
      else if (item.kind === 'sequencer') sequencerIds.add(item.sequencerId);
    }
  }
  return { macroIds, sequencerIds };
}

/** Build a project bundle: the project + its referenced macros +
 *  sequencers. Matrix excluded by design (see file header). */
export function buildProjectBundle(project: Project): ProjectBundle {
  const { macroIds, sequencerIds } = collectReferencedIds(project);
  const allMacros = macroLibrary();
  const allSequencers = sequencerLibrary();
  return {
    schema: BUNDLE_SCHEMA,
    exportedAt: Date.now(),
    origin: ORIGIN,
    kind: 'project',
    project: JSON.parse(JSON.stringify(project)) as Project,
    macros: {
      schemaVersion: allMacros.schemaVersion,
      macros: allMacros.macros.filter((m) => macroIds.has(m.id)),
    },
    sequencers: {
      schemaVersion: allSequencers.schemaVersion,
      sequencers: allSequencers.sequencers.filter((s) => sequencerIds.has(s.id)),
    },
    // Timelines belong to their project (DEC-235), so they travel with it.
    timelines: {
      schemaVersion: timelineLibrary().schemaVersion,
      timelines: clone(timelinesForProject(project.id, project.pages, timelineLibrary().timelines)),
    },
  };
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/** The allow-listed per-device settings this browser has set, raw. */
function settingsHere(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof localStorage === 'undefined') return out;
  for (const key of RESTORABLE_SETTING_KEYS) {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) out[key] = v;
    } catch { /* storage blocked - nothing to carry */ }
  }
  return out;
}

/** Build an "all" bundle: everything this browser holds that a wiped browser
 *  would need back (plan 3.2, 2026-10-01: "exports should include
 *  everything") - projects, every library, timelines, Rig States, the bridge
 *  pairings (bearers included) and this device's preferences. A lighting
 *  setup lives on OPBridge, so the pairing is what restores it. */
export function buildAllBundle(projects: Project[]): AllBundle {
  return {
    schema: BUNDLE_SCHEMA,
    exportedAt: Date.now(),
    origin: ORIGIN,
    kind: 'all',
    projects: clone(projects),
    macros: clone(macroLibrary()),
    sequencers: clone(sequencerLibrary()),
    matrix: clone(matrixLibrary()),
    timelines: clone(timelineLibrary()),
    rigStates: clone(rigStateLibrary()),
    pairings: clone(bridges()),
    settings: settingsHere(),
  };
}

// ─── Apply (import) ──────────────────────────────────────────────

/** Result of importing a project bundle - surfaces what the user
 *  actually got so the import UX can summarize. */
export interface ProjectImportResult {
  /** The project id as it landed in the store (may differ from
   *  bundle.project.id if "import as copy" assigned a new one). */
  projectId: string;
  /** The chosen project name (may have " (imported)" suffix). */
  projectName: string;
  /** Macros added or updated (a NEWER copy replaces this device's). */
  macrosAdded: number;
  /** Sequencers added or updated (same rule). */
  sequencersAdded: number;
  /** Timelines added (since 2026-10-01). */
  timelinesAdded: number;
  /** True if the project's id was already taken and we forked a copy. */
  forkedAsCopy: boolean;
}

/**
 * What a file brings that this device should take: entries it lacks, and
 * entries strictly NEWER than its own. A tie keeps what is here - an entry
 * with no stamp on either side must not be replaced by a file's copy.
 *
 * Adopted through the stores' `applySynced*` paths, NOT their authoring
 * setters (2026-10-01). The setters stamp what changed as edited NOW, so an
 * imported backup looked newer than everything and beat newer copies held by
 * OPBridge at the next sync - quietly undoing work. The adopt paths keep each
 * entry's own stamp.
 */
function newerOrNew<T extends { id: string }>(
  local: readonly T[],
  incoming: readonly T[] | undefined,
  stamp: (e: T) => number,
): T[] {
  const here = new Map(local.map((e) => [e.id, e]));
  return (incoming ?? []).filter((e) => {
    if (!e || typeof e.id !== 'string') return false;
    const mine = here.get(e.id);
    return !mine || stamp(e) > stamp(mine);
  });
}
const libStamp = (e: { modifiedAt?: number }) => e.modifiedAt ?? 0;
const timelineStamp = (t: Timeline) => {
  const n = Date.parse(t.modified ?? '');
  return Number.isFinite(n) ? n : 0;
};

function adoptMacros(incoming: MacroLibrary | undefined): number {
  return applySyncedMacros(newerOrNew(macroLibrary().macros, incoming?.macros, libStamp));
}

function adoptSequencers(incoming: SequencerLibrary | undefined): number {
  return applySyncedSequencers(newerOrNew(sequencerLibrary().sequencers, incoming?.sequencers, libStamp));
}

/**
 * Timelines from a file. `copies` maps a project id in the file to the id of
 * the copy it became here (its id was taken). A timeline of a copied project
 * that this device already has STAYS IN ITS PROJECT - moving it to the copy
 * would take it out of the original's list; the copy's Launchers still find
 * it by id. If the file's copy is newer, its content is still taken, in that
 * same project. One this device lacks goes to the copy.
 */
function adoptTimelines(incoming: readonly Timeline[] | undefined, copies: ReadonlyMap<string, string>): number {
  const here = new Map(timelineLibrary().timelines.map((t) => [t.id, t]));
  const remapped: Timeline[] = [];
  for (const t of incoming ?? []) {
    if (!t || typeof t.id !== 'string') continue;
    const copy = t.projectId ? copies.get(t.projectId) : undefined;
    if (copy === undefined) { remapped.push(t); continue; }
    const mine = here.get(t.id);
    const owner = mine ? mine.projectId : copy;
    const { projectId: _fileOwner, ...rest } = t;
    remapped.push(owner !== undefined ? { ...rest, projectId: owner } : rest);
  }
  const take = newerOrNew(timelineLibrary().timelines, remapped, timelineStamp);
  if (take.length === 0) return 0;
  return applySyncedTimelines(take, []).adopted;
}

/** Import a project bundle. Always imports as a copy: the project gets
 *  a fresh id + " (imported)" suffix so it never overwrites an existing
 *  one. Macros, sequencers and timelines come in by the newer-or-new rule
 *  above. Matrix is NOT touched - bundle.project's items keep
 *  their (channel, ccNumber) values verbatim but those will appear
 *  unclaimed in the importer's matrix until the user re-claims them. */
export function importProjectBundle(
  bundle: ProjectBundle,
  insertProject: (project: Project, makeActive: boolean) => string,
  makeActive: boolean,
): ProjectImportResult {
  const forkedProject: Project = {
    ...bundle.project,
    id: newProjectId(),
    name: `${bundle.project.name} (imported)`,
    createdAt: Date.now(),
    pages: bundle.project.pages.map((pg) => ({ ...pg, id: newPageId() })),
    activePageId: '', // set right after we know the new page ids
  };
  forkedProject.activePageId = forkedProject.pages[0]?.id ?? '';
  // Update the project's items that reference page ids... actually,
  // items don't reference page ids (they live inside pages), so no
  // remapping needed beyond regenerating ids themselves.

  const macrosAdded = adoptMacros(bundle.macros);
  const sequencersAdded = adoptSequencers(bundle.sequencers);
  const newId = insertProject(forkedProject, makeActive);
  const timelinesAdded = adoptTimelines(bundle.timelines?.timelines, new Map([[bundle.project.id, newId]]));

  return {
    projectId: newId,
    projectName: forkedProject.name,
    macrosAdded,
    sequencersAdded,
    timelinesAdded,
    forkedAsCopy: true,
  };
}

export interface AllImportResult {
  /** Put back as they were: same id, same name (the id was free here). */
  projectsRestored: number;
  /** The id was taken here, so it landed as an "(imported)" copy. */
  projectsCopied: number;
  macrosAdded: number;
  sequencersAdded: number;
  matrixReplaced: boolean;
  timelinesAdded: number;
  rigStatesAdded: number;
  bridgesAdded: number;
  settingsRestored: number;
}

/** Apply one restored setting: write it, and make it live where the app
 *  holds it in a signal. The transport offset is read when the header
 *  mounts, so it applies on the next load. */
function applySetting(key: string, raw: string): void {
  try { localStorage.setItem(key, raw); } catch { /* storage blocked */ }
  try {
    switch (key) {
      case 'opbuttons.skin.v1':
        if (SKINS.some((s) => s.id === raw)) setActiveSkin(raw as SkinName);
        break;
      case 'opbuttons.barPosition.v1':
        setBarPosition(raw === 'bottom' ? 'bottom' : 'top');
        break;
      case 'opbuttons.editTriggers.v1':
        setEditTriggersEnabled(raw === 'on');
        break;
      case 'opbuttons.screenDim.v2': {
        const n = parseFloat(raw);
        if (Number.isFinite(n)) setStageDim(n);
        break;
      }
      case 'opbuttons.preflight.thresholds.v1': {
        const t = JSON.parse(raw) as Partial<Record<keyof PreflightThresholds, unknown>>;
        for (const [k, v] of Object.entries(t)) {
          if (typeof v === 'number') setThreshold(k as keyof PreflightThresholds, v);
        }
        break;
      }
      case 'opbuttons.helix.setlistNames.v1': {
        const names = JSON.parse(raw) as Record<string, unknown>;
        for (const [k, v] of Object.entries(names)) {
          const n = Number(k);
          if (Number.isInteger(n) && typeof v === 'string') setHelixSetlistOverride(n, v);
        }
        break;
      }
      default:
        break;
    }
  } catch { /* a malformed value stays written raw; its own loader falls back */ }
}

/**
 * "Import everything" = RESTORE a backup (plan 3.2, 2026-10-01; done-test:
 * "export, wipe a test browser, import; every timeline, Rig State, lighting
 * setup and pairing is back").
 *
 * - Projects come back AS THEY WERE (same id, same name, own `modifiedAt`)
 *   when the id is free here. Before this every project was forked with a new
 *   id and " (imported)", so a restored browser that paired again showed each
 *   project twice (the synced original and the copy), and the restored
 *   timelines lost their project. A project whose id IS taken still lands as
 *   an "(imported)" copy, as before.
 * - Macros, sequencers, timelines: newer-or-new, never re-stamped.
 * - Rig States: banks by id, a newer bank replaces an older one.
 * - Pairings: added back with their row ids unless this device already pairs
 *   with that OPBridge; that is also what brings a lighting setup back, since
 *   fixtures, patch and groups live on OPBridge.
 * - Settings: only those this device has not set itself (per-device prefs).
 * - Matrix: replaced when `replaceMatrix` and the file's matrix is not empty.
 */
export function importAllBundle(
  bundle: AllBundle,
  insertProject: (project: Project, makeActive: boolean) => string,
  replaceMatrix: boolean,
): AllImportResult {
  const macrosAdded = adoptMacros(bundle.macros);
  const sequencersAdded = adoptSequencers(bundle.sequencers);
  let matrixReplaced = false;
  // An empty matrix in the file is "this backup has no rig", not "delete
  // mine": replacing with it wiped the device's matrix (and, since S305's
  // per-record sync, would delete every record on the other PCs too).
  const m = bundle.matrix;
  if (replaceMatrix && m && (m.devices?.length || m.slots?.length)) {
    setMatrixLibrary(bundle.matrix);
    matrixReplaced = true;
  }

  const taken = new Set(allProjects().map((p) => p.id));
  const copies = new Map<string, string>();
  let projectsRestored = 0;
  let projectsCopied = 0;
  let firstRestored: string | null = null;
  for (const p of bundle.projects) {
    if (!taken.has(p.id)) {
      restoreProject(p);
      taken.add(p.id);
      firstRestored ??= p.id;
      projectsRestored++;
      continue;
    }
    const forked: Project = {
      ...p,
      id: newProjectId(),
      name: `${p.name} (imported)`,
      createdAt: Date.now(),
      pages: p.pages.map((pg) => ({ ...pg, id: newPageId() })),
      activePageId: '',
    };
    forked.activePageId = forked.pages[0]?.id ?? '';
    copies.set(p.id, insertProject(forked, false));
    projectsCopied++;
  }
  // A wiped browser has no project open; open the first one put back.
  if (activeProjectId() === null && firstRestored) switchProject(firstRestored);

  const timelinesAdded = adoptTimelines(bundle.timelines?.timelines, copies);

  let rigStatesAdded = 0;
  const rig = mergeRigStateBanks(rigStateLibrary(), bundle.rigStates as RigStateLibrary | undefined);
  if (rig.next) {
    setRigStateLibrary(rig.next);
    rigStatesAdded = rig.added + rig.updated;
  }

  const bridgesAdded = restoreBridges(planBridgeRestore(bridges(), bundle.pairings).add);

  const settings = settingsToRestore(bundle.settings, (key) => {
    try { return localStorage.getItem(key) !== null; } catch { return true; }
  });
  for (const [key, raw] of Object.entries(settings)) applySetting(key, raw);

  return {
    projectsRestored,
    projectsCopied,
    macrosAdded,
    sequencersAdded,
    matrixReplaced,
    timelinesAdded,
    rigStatesAdded,
    bridgesAdded,
    settingsRestored: Object.keys(settings).length,
  };
}

/** "Import everything" from a file's text - the one path Settings and the
 *  first-run welcome view ("Restore from a backup") both use. Refuses a file
 *  that is not a full bundle, with a reason. */
export function importEverythingFromText(text: string):
  | { ok: true; result: AllImportResult; message: string }
  | { ok: false; message: string } {
  const v = parseBundle(text);
  if (!v.ok) return { ok: false, message: `Invalid bundle: ${v.reason}` };
  if (v.bundle.kind !== 'all') {
    return {
      ok: false,
      message: `This file is "${v.bundle.kind}" - use Import project for single-project bundles, or export with "Export everything" to make a full bundle.`,
    };
  }
  const result = importAllBundle(v.bundle, (p, makeActive) => insertProject(p, makeActive), true);
  return { ok: true, result, message: describeAllImport(result) };
}

/** One line saying what an "Import everything" did, for a toast or flash. */
export function describeAllImport(r: AllImportResult): string {
  const bits: string[] = [];
  if (r.projectsRestored) bits.push(`${r.projectsRestored} project(s) restored`);
  if (r.projectsCopied) bits.push(`${r.projectsCopied} project(s) already here, added as "(imported)" copies`);
  if (r.timelinesAdded) bits.push(`${r.timelinesAdded} timeline(s)`);
  if (r.macrosAdded) bits.push(`${r.macrosAdded} macro(s)`);
  if (r.sequencersAdded) bits.push(`${r.sequencersAdded} sequencer(s)`);
  if (r.rigStatesAdded) bits.push(`${r.rigStatesAdded} Rig State(s)`);
  if (r.bridgesAdded) bits.push(`${r.bridgesAdded} bridge pairing(s)`);
  if (r.settingsRestored) bits.push(`${r.settingsRestored} setting(s)`);
  if (r.matrixReplaced) bits.push('matrix replaced');
  return bits.length ? `Imported everything: ${bits.join(', ')}.` : 'Imported everything: nothing new - this device already has all of it.';
}

// ─── Local helpers ───────────────────────────────────────────────

function newProjectId(): string {
  return `prj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function newPageId(): string {
  return `page-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
